import { baseApi } from "./base-api";
import { ApiEndpoints, buildEndpoint } from "@_constants/endpoints";
import type {
  Dataset,
  DatasetsListResponse,
  DatasetStateFilter,
  CreateDatasetRequest,
  PatchDatasetRequest,
  QuestionsListForChart,
  DatasetQuestionsResponse,
  DatasetQuestionsPaginatedResponse,
  WeightConfigurationRequest,
  WeightConfigurationResponse,
  DatasetReconciliationDetailsResponse,
  DatasetQuestionsListResponse,
  VisibilityFilter,
  UpdateDatasetElementsVisibilityRequest,
  UpdateDatasetElementsVisibilityResponse,
} from "@models/Dataset";
import { normalizeDateGuardrails } from "@models/Dataset";
import type {
  QuestionVariablesResponse,
  QuestionResponsesSearchFilter,
} from "@models/Filter";
import { parseBaseConfigJson } from "@models/DatasetBase";
import { parseMetricsConfigJson } from "@models/Metric";

// Backend stores date guardrails, dataset bases, and dataset metrics as opaque
// JSON strings. Parse them into their typed shapes so consumers only ever see
// dataset.dateGuardrails / dataset.bases / dataset.metrics, never the raw
// *ConfigJson wire fields.
type DatasetConfigWire = Dataset & {
  dateGuardrailsConfigJson?: string | null;
  baseConfigJson?: string | null;
  metricsConfigJson?: string | null;
};

function transformDatasetResponse(response: DatasetConfigWire): Dataset {
  const { dateGuardrailsConfigJson, baseConfigJson, metricsConfigJson, ...rest } = response;
  let dateGuardrails: Dataset["dateGuardrails"] = null;
  if (dateGuardrailsConfigJson) {
    try {
      dateGuardrails = normalizeDateGuardrails(JSON.parse(dateGuardrailsConfigJson));
    } catch {
      console.warn("Failed to parse dateGuardrailsConfigJson");
    }
  }
  return {
    ...rest,
    dateGuardrails,
    bases: parseBaseConfigJson(baseConfigJson),
    metrics: parseMetricsConfigJson(metricsConfigJson),
  };
}

// The dataset-elements API returns `isHidden` on questions/variables/values;
// the frontend models use `hidden`, so map the field name coming off the wire.
function mapElementHidden<T extends { isHidden?: boolean }>(element: T): T & { hidden?: boolean } {
  return { ...element, hidden: element.isHidden };
}

function transformDatasetQuestionsListResponse(response: any): DatasetQuestionsListResponse {
  if (!response || typeof response !== "object" || !Array.isArray(response.questions)) {
    return {
      questions: [],
      pagination: {
        page: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
  return {
    ...response,
    questions: response.questions.map((question: any) => ({
      ...mapElementHidden(question),
      rowVariables: (question.rowVariables || []).map(mapElementHidden),
      columnVariables: (question.columnVariables || []).map(mapElementHidden),
      values: (question.values || []).map(mapElementHidden),
    })),
  };
}

export const datasetApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDatasets: builder.query<Dataset[], { state?: DatasetStateFilter; page_size?: number }>({
      query: ({ state = "all", page_size } = {}) => ({
        url: ApiEndpoints.AGGREGATION_DATASETS,
        params: {
          ...(state !== "all" ? { state } : {}),
          ...(page_size !== undefined ? { page_size } : {}),
        },
      }),
      providesTags: ["Dataset"],
      keepUnusedDataFor: 24 * 60 * 60,
      transformResponse: (
        response: DatasetsListResponse | Dataset[],
      ): Dataset[] => {
        console.log("📊 RTK Query: getDatasets response received");
        // Handle paginated response shape: { datasets: Dataset[], pagination: ... }
        if (!Array.isArray(response) && Array.isArray(response.datasets)) {
          return response.datasets;
        }
        // Fallback for plain array response
        if (Array.isArray(response)) {
          return response;
        }
        console.warn(
          "getDatasets: unexpected API response shape, returning empty array",
        );
        return [];
      },
      transformErrorResponse: (response: any) => {
        console.error("❌ RTK Query: getDatasets error:", response);
        return response;
      },
    }),
    getDatasetsPaginated: builder.query<
      DatasetsListResponse,
      {
        state?: DatasetStateFilter;
        name?: string;
        page?: number;
        page_size?: number;
      }
    >({
      query: ({ state = "active", name, page = 1, page_size = 10 } = {}) => ({
        url: ApiEndpoints.AGGREGATION_DATASETS,
        params: {
          ...(state !== "all" && { state }),
          ...(name && { name }),
          page,
          page_size,
        },
      }),
      providesTags: ["Dataset"],
      transformResponse: (
        response: DatasetsListResponse | Dataset[],
      ): DatasetsListResponse => {
        if (!Array.isArray(response) && Array.isArray(response.datasets)) {
          return response;
        }
        // Fallback: plain array (legacy)
        const datasets = Array.isArray(response) ? response : [];
        return {
          datasets,
          pagination: {
            page: 1,
            pageSize: datasets.length,
            totalItems: datasets.length,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        };
      },
      transformErrorResponse: (response: any) => {
        console.error("❌ RTK Query: getDatasetsPaginated error:", response);
        return response;
      },
    }),
    getDatasetById: builder.query<Dataset, string>({
      query: (id) => ({
        url: buildEndpoint(ApiEndpoints.AGGREGATION_DATASETS_BY_ID, { id }),
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Dataset", id }],
      transformResponse: transformDatasetResponse,
    }),
    createDataset: builder.mutation<Dataset, CreateDatasetRequest>({
      query: (body) => ({
        url: ApiEndpoints.AGGREGATION_DATASETS,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Dataset"],
    }),
    patchDataset: builder.mutation<Dataset, PatchDatasetRequest>({
      query: ({ id, ...body }) => ({
        url: buildEndpoint(ApiEndpoints.AGGREGATION_DATASETS_BY_ID, { id }),
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Dataset", "AggregationSurvey"],
      transformResponse: transformDatasetResponse,
    }),
    saveDatasetConfiguration: builder.mutation<
      Dataset,
      { id: string; clientId?: string; researchContext?: string }
    >({
      query: ({ id, clientId, researchContext }) => {
        // Build the body object only with provided values
        const body: { clientId?: string; researchContext?: string } = {};
        if (clientId !== undefined && clientId !== "") {
          body.clientId = clientId;
        }
        if (researchContext !== undefined && researchContext !== "") {
          body.researchContext = researchContext;
        }

        return {
          url: buildEndpoint(ApiEndpoints.AGGREGATION_DATASETS_BY_ID, { id }),
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (_, __, { id }) => [{ type: "Dataset", id }, "Dataset"],
    }),
    getDatasetQuestions: builder.query<
      DatasetQuestionsPaginatedResponse,
      {
        id: string;
        survey_id?: string;
        page?: number;
        page_size?: number;
        question_type?: string[];
        search_query?: string;
        multidimensional?: boolean;
        dataset_element_id?: string;
        dataset_element_ids?: string[];
        /** Server-side visibility filter — pass `false` to exclude hidden questions
         *  (e.g. chart-authoring question pickers). Omit to get every question,
         *  which is required when resolving an already-selected question by id. */
        is_hidden?: boolean;
      }
    >({
      query: ({
        id,
        survey_id,
        page = 1,
        page_size = 10,
        question_type,
        search_query,
        multidimensional = true,
        dataset_element_id = "",
        dataset_element_ids,
        is_hidden,
      }) => ({
        url: buildEndpoint(ApiEndpoints.AGGREGATION_DATASETS_QUESTIONS, { id }),
        method: "GET",
        params: {
          survey_id,
          page,
          page_size,
          question_type,
          search_query,
          multidimensional,
          dataset_element_id,
          ...(dataset_element_ids &&
            dataset_element_ids.length > 0 && { dataset_element_ids }),
          ...(is_hidden !== undefined && { is_hidden }),
        },
      }),
      transformResponse: (
        response: DatasetQuestionsPaginatedResponse,
      ): DatasetQuestionsPaginatedResponse => {
        // Handle paginated response structure
        if (
          response &&
          response.pagination &&
          Array.isArray(response.questions)
        ) {
          return {
            // The raw response reports visibility as `isHidden`; every
            // consumer reads `.hidden`, so normalize it here (same as the
            // admin questions-list transform does via mapElementHidden). The
            // declared type uses `hidden`, so cast to expose the raw field.
            questions: (response.questions || []).map(
              (q) => mapElementHidden(q as typeof q & { isHidden?: boolean }),
            ),
            pagination: response.pagination,
          };
        }

        console.warn(
          "⚠️ Invalid response structure, returning default pagination",
        );
        return {
          questions: [],
          pagination: {
            page: 1,
            pageSize: 10,
            totalItems: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        };
      },
      transformErrorResponse: (response: any) => {
        console.error("❌ RTK Query: getDatasetQuestions error:", response);
        return response;
      },
      providesTags: (_, __, { id }) => [{ type: "Dataset", id }],
    }),
    // use any as response type since the mock data is extremely custom
    getDatasetQuestionsResponse: builder.mutation<
      QuestionsListForChart,
      {
        id: string;
        datasetId: string;
        topN?: number | null;
        sorting?: "ORIGINAL" | "ASCENDING" | "DESCENDING";
        weightSelection?: "DISABLED" | "SURVEY_WEIGHT" | "DATASET_DEFAULT";
        chartType?:
          | "BAR"
          | "PIE"
          | "LINE"
          | "DONUT"
          | "TABLE"
          | "GROUPED_BAR"
          | "STACKED_BAR"
          | "SCORECARD";
        filters?: QuestionResponsesSearchFilter[];
        focusMode?: string;
        focusEntity?: string;
        focusRollup?: import("@models/Rollup").WidgetRollup;
        categoryRollups?: import("@models/Rollup").WidgetRollup[] | null;
        timeUnit?: "QUARTER" | "SEMESTER" | "MONTH" | "WEEK";
        startDate?: string;
        endDate?: string;
        pValue?: number;
        minimumSampleSize?: number;
        enableStatTesting?: boolean;
        statTestingType?: "Z-TEST" | "T-TEST";
        useTotalBase?: boolean;
        periodCount?: number;
        // Breakout params
        enableLineBreakout?: boolean;
        breakoutSourceLine?: string;
        crossQuestionId?: string;
        crossFocusMode?: string;
        crossFocusEntity?: string;
        crossFocusRollup?: import("@models/Rollup").WidgetRollup;
      }
    >({
      query: ({
        id,
        datasetId,
        //later change to 10
        topN = 100,
        sorting = "ORIGINAL",
        weightSelection = "DISABLED",
        chartType = "BAR",
        filters,
        focusMode,
        focusEntity,
        focusRollup,
        categoryRollups,
        timeUnit,
        startDate,
        endDate,
        pValue,
        minimumSampleSize,
        enableStatTesting,
        statTestingType,
        periodCount,
        enableLineBreakout,
        breakoutSourceLine,
        crossQuestionId,
        crossFocusMode,
        crossFocusEntity,
        crossFocusRollup,
        useTotalBase,
      }) => ({
        url: buildEndpoint(
          ApiEndpoints.AGGREGATION_DATASETS_QUESTIONS_RESPONSE,
          { id, datasetId },
        ),
        method: "POST",
        body: {
          questionId: id,
          datasetId,
          topN,
          sorting,
          weightSelection,
          chartType,
          ...(enableStatTesting && { enableStatTesting: true }),
          ...(statTestingType && { statTestingType }),
          ...(filters && filters.length > 0 && { filters }),
          ...(focusMode && { focusMode }),
          ...(focusEntity && { focusEntity }),
          ...(focusRollup && { focusRollup }),
          ...(categoryRollups && categoryRollups.length > 0 && { categoryRollups }),
          ...(timeUnit && { timeUnit }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(pValue !== undefined && { pValue }),
          ...(minimumSampleSize !== undefined && { minimumSampleSize }),
          ...(periodCount !== undefined && { periodCount }),
          ...(enableLineBreakout && { enableLineBreakout: true }),
          ...(breakoutSourceLine && { breakoutSourceLine }),
          ...(crossQuestionId && { crossQuestionId }),
          ...(crossFocusMode && { crossFocusMode }),
          ...(crossFocusEntity && { crossFocusEntity }),
          ...(crossFocusRollup && { crossFocusRollup }),
          ...(useTotalBase && { useTotalBase: true }),
        },
      }),
    }),
    // New endpoint for reconciliation details
    getDatasetReconciliationDetails: builder.query<
      DatasetReconciliationDetailsResponse,
      string
    >({
      query: (id) => ({
        url: buildEndpoint(
          ApiEndpoints.AGGREGATION_DATASETS_RECONCILIATION_DETAILS,
          { id },
        ),
        method: "GET",
      }),
      providesTags: (_, __, id) => [{ type: "Dataset", id }],
      transformResponse: (
        response: any,
      ): DatasetReconciliationDetailsResponse => {
        if (!response || typeof response !== "object") {
          return {
            id: "",
            totalQuestionCount: 0,
            matchedQuestionCount: 0,
            unmatchedQuestionCount: 0,
            variablesCount: 0,
            orphanedVariablesCount: 0,
            valuesCount: 0,
            totalResponseCount: 0,
            totalSurveyCount: 0,
          };
        }
        return response;
      },
      transformErrorResponse: (response: any) => {
        console.error(
          "❌ RTK Query: getDatasetReconciliationDetails error:",
          response,
        );
        return response;
      },
    }),
    // New endpoint for questions list with pagination
    getDatasetQuestionsList: builder.query<
      DatasetQuestionsListResponse,
      {
        id: string;
        page?: number;
        page_size?: number;
        search_query?: string;
        visibility?: VisibilityFilter;
        expanded?: boolean;
        dataset_element_id?: string;
        /** Fetch several specific questions' full (expanded) detail in one
         *  request — same backend route/param as getDatasetQuestions'
         *  dataset_element_ids, just combined here with `expanded` so the
         *  response includes rowVariables/values too. */
        dataset_element_ids?: string[];
      }
    >({
      query: ({
        id,
        page = 1,
        page_size = 10,
        search_query,
        visibility,
        dataset_element_id,
        dataset_element_ids,
        expanded = true,
      }) => ({
        url: buildEndpoint(ApiEndpoints.AGGREGATION_DATASETS_QUESTIONS, { id }),
        method: "GET",
        params: {
          page,
          page_size,
          ...(search_query && { search_query }),
          // `is_hidden` deliberately omitted — child_element_is_hidden now
          // excludes questions with no matching children server-side, so it
          // alone gives the full union (hidden questions + visible questions
          // with a matching hidden/visible child) with correct pagination.
          ...(visibility === "visible" && { child_element_is_hidden: false }),
          ...(visibility === "hidden" && { child_element_is_hidden: true }),
          ...(expanded && { expanded }),
          ...(dataset_element_id && { dataset_element_id }),
          ...(dataset_element_ids &&
            dataset_element_ids.length > 0 && { dataset_element_ids }),
        },
      }),
      providesTags: (_, __, { id }) => [{ type: "Dataset", id }],
      transformResponse: transformDatasetQuestionsListResponse,
      transformErrorResponse: (response: any) => {
        console.error("❌ RTK Query: getDatasetQuestionsList error:", response);
        return response;
      },
    }),
    // Curate-content module: bulk hide/show for questions, variables, and values
    updateDatasetElementsVisibility: builder.mutation<
      UpdateDatasetElementsVisibilityResponse,
      UpdateDatasetElementsVisibilityRequest
    >({
      query: ({ datasetId, updates }) => ({
        url: buildEndpoint(ApiEndpoints.AGGREGATION_DATASET_ELEMENTS, {
          id: datasetId,
        }),
        method: "PATCH",
        body: { updates },
      }),
      invalidatesTags: (_, __, { datasetId }) => [
        { type: "Dataset", id: datasetId },
      ],
    }),
    // Legacy endpoint - kept for backward compatibility
    getDatasetQuestionsDetails: builder.query<DatasetQuestionsResponse, string>(
      {
        query: (id) => ({
          url: buildEndpoint(
            ApiEndpoints.AGGREGATION_DATASETS_RECONCILIATION_DETAILS,
            { id },
          ),
          method: "GET",
        }),
        providesTags: (_, __, id) => [{ type: "Dataset", id }],
        transformResponse: (response: any): DatasetQuestionsResponse => {
          if (!response || typeof response !== "object") {
            return {
              id: "",
              totalQuestionCount: 0,
              matchedQuestionCount: 0,
              variablesCount: 0,
              orphanedVariablesCount: 0,
              valuesCount: 0,
              totalResponseCount: 0,
              totalSurveyCount: 0,
              datasetStatus: "QUEUED",
              questions: [],
              orphanedVariables: [],
            };
          }
          return response;
        },
        transformErrorResponse: (response: any) => {
          console.error("❌ RTK Query: getDatasetQuestions error:", response);
          return response;
        },
      },
    ),
    getDatasetStatus: builder.query<
      {
        datasetId: string;
        reconciliationStatus: string;
        lastUpdated: string;
        errorMessage?: string;
      },
      string
    >({
      query: (id) => ({
        url: buildEndpoint(ApiEndpoints.AGGREGATION_DATASETS_STATUS, { id }),
        method: "GET",
      }),
      transformResponse: (
        response: any,
      ): {
        datasetId: string;
        reconciliationStatus: string;
        lastUpdated: string;
        errorMessage?: string;
      } => {
        console.log("📊 RTK Query: getDatasetStatus response received");
        if (!response || typeof response !== "object") {
          console.warn(
            "getDatasetStatus: API response is not an object, returning default status",
          );
          return {
            datasetId: "",
            reconciliationStatus: "QUEUED",
            lastUpdated: new Date().toISOString(),
          };
        }
        return response;
      },
      transformErrorResponse: (response: any) => {
        console.error("❌ RTK Query: getDatasetStatus error:", response);
        return response;
      },
    }),
    getDatasetQuestionVariables: builder.query<
      QuestionVariablesResponse,
      //for not to confuse team jan - id is dataset_id here
      { id: string; question_id: string; variable_type?: string }
    >({
      query: ({ id, question_id, variable_type }) => ({
        url: buildEndpoint(
          ApiEndpoints.AGGREGATION_DATASETS_QUESTION_VARIABLES,
          { id, question_id },
        ),
        method: "GET",
        params: variable_type ? { variable_type } : {},
      }),
      // Same raw-isHidden-vs-.hidden mismatch as getDatasetQuestionVariablesByElement
      // below — normalize here too so dashboard filters' rollup/option pickers
      // (which read .hidden) see real values instead of always-undefined.
      transformResponse: (response: QuestionVariablesResponse) => {
        type RawHidden = { isHidden?: boolean; hidden?: boolean };
        const normalize = <T extends RawHidden>(item: T): T => ({
          ...item,
          hidden: item.isHidden ?? item.hidden,
        });
        return {
          variables: response.variables?.map(normalize),
          values: response.values?.map(normalize),
        };
      },
    }),
    getDatasetQuestionVariablesByElement: builder.query<
      QuestionVariablesResponse,
      { id: string; dataset_element_id: string }
    >({
      query: ({ id, dataset_element_id }) => ({
        url: buildEndpoint(
          ApiEndpoints.AGGREGATION_DATASETS_QUESTION_VARIABLES_BY_ELEMENT,
          { id, dataset_element_id },
        ),
        method: "GET",
      }),
      // The raw response carries visibility as `isHidden`, not `hidden` — every
      // consumer (chart-authoring hidden-tagging/filtering) reads `.hidden`, so
      // normalize it here rather than at each call site.
      transformResponse: (response: QuestionVariablesResponse) => {
        type RawHidden = { isHidden?: boolean; hidden?: boolean };
        const normalize = <T extends RawHidden>(item: T): T => ({
          ...item,
          hidden: item.isHidden ?? item.hidden,
        });
        return {
          variables: response.variables?.map(normalize),
          values: response.values?.map(normalize),
        };
      },
      providesTags: (_, __, { id }) => [{ type: "Dataset", id }],
    }),
    getWeightConfigurations: builder.query<WeightConfigurationResponse, string>(
      {
        query: (id) => ({
          url: buildEndpoint(
            ApiEndpoints.AGGREGATION_DATASETS_WEIGHT_CONFIGURATIONS,
            { id },
          ),
          method: "GET",
        }),
        providesTags: (result, error, id) => [{ type: "Dataset", id }],
        transformResponse: (response: any): WeightConfigurationResponse => {
          console.log(
            "📊 RTK Query: getWeightConfigurations response received",
          );
          if (!response || typeof response !== "object") {
            console.warn(
              "getWeightConfigurations: API response is not valid, returning default structure",
            );
            return {
              weightConfigurations: [],
            };
          }
          return response;
        },
        transformErrorResponse: (response: any) => {
          console.error(
            "❌ RTK Query: getWeightConfigurations error:",
            response,
          );
          return response;
        },
      },
    ),
    getDatasetTimeBounds: builder.query<{ minDate: string; maxDate: string }, string>({
      query: (id) => ({
        url: buildEndpoint(ApiEndpoints.AGGREGATION_DATASETS_TIME_BOUNDS, { id }),
        method: "GET",
      }),
    }),
    createWeightConfigurations: builder.mutation<
      WeightConfigurationResponse,
      { id: string; weightConfigurations: WeightConfigurationRequest }
    >({
      query: ({ id, weightConfigurations }) => ({
        url: buildEndpoint(
          ApiEndpoints.AGGREGATION_DATASETS_WEIGHT_CONFIGURATIONS,
          { id },
        ),
        method: "POST",
        body: weightConfigurations,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Dataset", id },
        "Dataset",
      ],
    }),
  }),
});

export const {
  useGetDatasetsQuery,
  useLazyGetDatasetsQuery,
  useGetDatasetsPaginatedQuery,
  useLazyGetDatasetsPaginatedQuery,
  useCreateDatasetMutation,
  usePatchDatasetMutation,
  useSaveDatasetConfigurationMutation,
  useGetDatasetByIdQuery,
  useLazyGetDatasetByIdQuery,
  useGetDatasetQuestionsQuery,
  useLazyGetDatasetQuestionsQuery,
  useGetDatasetQuestionsResponseMutation,
  useGetDatasetReconciliationDetailsQuery,
  useLazyGetDatasetReconciliationDetailsQuery,
  useGetDatasetQuestionsListQuery,
  useLazyGetDatasetQuestionsListQuery,
  useUpdateDatasetElementsVisibilityMutation,
  useGetDatasetQuestionsDetailsQuery,
  useLazyGetDatasetQuestionsDetailsQuery,
  useGetDatasetStatusQuery,
  useLazyGetDatasetStatusQuery,
  useGetDatasetQuestionVariablesQuery,
  useGetDatasetQuestionVariablesByElementQuery,
  useLazyGetDatasetQuestionVariablesByElementQuery,
  useGetWeightConfigurationsQuery,
  useLazyGetWeightConfigurationsQuery,
  useCreateWeightConfigurationsMutation,
  useGetDatasetTimeBoundsQuery,
} = datasetApi;

export default datasetApi;
